import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Download, Share2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api, downloadFile, errMsg, fmtDate, fmtDateTime } from '../api';
import { PageHeader, Card, SectionTitle, Spinner, goldBtn, ghostBtn } from '../ui';

const FLAGS = [['hasProtesti', 'Protesti'], ['hasSevereProtesti', 'Protesti gravi'], ['hasPrejudicial', 'Pregiudizievoli'], ['hasSeverePrejudicial', 'Pregiudizievoli gravi']];
const CO_FLAGS = [['hasProtestiForCompany', 'Protesti'], ['hasSevereProtestiForCompany', 'Protesti gravi'], ['hasPrejudicialForCompany', 'Pregiudizievoli'], ['hasSeverePrejudicialForCompany', 'Pregiudizievoli gravi']];
const STATUS = { Active: 'Attiva', NonActive: 'Cessata', Inactive: 'Cessata', Dissolved: 'Cessata' };
const RED = 'text-[#E5484D] border-[#E5484D]/50 bg-[#E5484D]/[0.08]';
const GREEN = 'text-[#2E9E62] border-[#2E9E62]/50 bg-[#2E9E62]/[0.08]';
const cap = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : s);

const parsePosition = (p) => {
  const raw = (p.positionName || p.additionalData?.positionDetail || '').trim();
  const m = raw.match(/(.*?)\s+(?:DATA NOMINA|DAL:?)\s+(\d{2}\/\d{2}\/\d{4})(.*)$/);
  if (!m) return { role: cap(raw), since: p.dateAppointed ? fmtDate(p.dateAppointed) : null, duration: null };
  const d = m[3].match(/DURATA:?\s*(.+)$/);
  return { role: cap(m[1].trim()), since: m[2], duration: d ? d[1].replace(/^[\s,]+|[\s,]+$/g, '').toLowerCase() : null };
};

export function Flag({ on, label, testid }) {
  return <span className={`text-[11px] px-2 py-0.5 border rounded-sm font-medium ${on ? RED : GREEN}`} data-testid={testid}>{label}: {on ? 'presenti' : 'nessuno'}</span>;
}

function Company({ c }) {
  const ad = c.additionalData || {};
  const status = STATUS[c.status] || c.status;
  const info = [['Codice fiscale', ad.taxCode || c.companyRegistrationNumber], ['REA', ad.cciaaRea], ['Forma giuridica', c.legalForm?.description], ['Attività', c.activity?.description],
    ['PEC', ad.legalEmail], ['Sede', c.address?.simpleValue], ['Inizio attività', ad.activityStartDate && fmtDate(ad.activityStartDate)]].filter(([, v]) => v);
  return (
    <div className="py-5 first:pt-0" data-testid={`people-company-${c.id}`}>
      <div className="flex flex-wrap items-baseline gap-3 mb-3">
        <p className="text-base text-[#F2F2F2] font-medium">{c.companyName}</p>
        <span className={`text-[10px] font-mono tracking-wider px-2 py-0.5 border rounded-sm ${status === 'Attiva' ? GREEN : 'text-[#8A8F97] border-[#3A3F47]'}`}>{status}</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 mb-3">
        {info.map(([l, v]) => <div key={l}><p className="text-[10px] uppercase tracking-[0.14em] text-[#5A5F67]">{l}</p><p className="text-xs text-[#C9CCD1] break-words">{v}</p></div>)}
      </div>
      <p className="text-[10px] uppercase tracking-[0.14em] text-[#5A5F67] mb-1">Cariche</p>
      <ul className="space-y-1 mb-3">
        {(c.positions || []).map((p, i) => { const r = parsePosition(p); return (
          <li key={i} className="text-sm text-[#C9CCD1]"><span className="text-[#F2F2F2]">{r.role || 'Carica'}</span>{r.since && <span className="text-[#8A8F97]"> · dal {r.since}</span>}{r.duration && <span className="text-[#8A8F97]"> · durata: {r.duration}</span>}</li>
        ); })}
      </ul>
      <div className="flex flex-wrap gap-2">{CO_FLAGS.map(([k, l]) => <Flag key={k} on={!!ad[k]} label={l} testid={`people-company-flag-${c.id}-${k}`} />)}</div>
    </div>
  );
}

function Companies({ title, items, testid }) {
  return (
    <Card className="p-6 mb-6" data-testid={testid}>
      <SectionTitle>{title} · {items.length}</SectionTitle>
      {!items.length ? <p className="text-sm text-[#5A5F67]">Nessuna carica.</p> : <div className="divide-y divide-[#1F242C]">{items.map((c, i) => <Company key={`${c.id}-${i}`} c={c} />)}</div>}
    </Card>
  );
}

export default function PeopleReportPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.get(`/cs/people/reports/${id}`).then((r) => setDoc(r.data)).catch((e) => { toast.error(errMsg(e)); setDoc({}); }); }, [id]);
  if (!doc) return <Spinner />;
  const r = doc.report;
  if (!r) return <Card className="p-6"><p className="text-sm text-[#8A8F97]" data-testid="people-report-expired">Il contenuto del report non è più disponibile (i report restano consultabili per 7 giorni).</p></Card>;
  const det = r.directorDetails || {}; const ad = det.additionalData || {}; const cf = det.id || doc.people_id?.replace(/^IT-/, '');
  const anyNeg = FLAGS.some(([k]) => ad[k]);
  return (
    <div data-testid="people-report-page">
      <PageHeader kicker="Report esponente" title={det.name || doc.name} />
      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={() => downloadFile(`/cs/people/reports/${id}/pdf`, `BOND_esponente_${cf}.pdf`).catch((e) => toast.error(errMsg(e)))} className={`${goldBtn} h-10 px-4 inline-flex items-center gap-2 text-sm`} data-testid="people-report-pdf"><Download size={15} />Scarica PDF</button>
        <Link to={`/portal/grafo?person=${cf}`} className={`${ghostBtn} h-10 px-4 inline-flex items-center gap-2 text-sm`} data-testid="people-report-graph"><Share2 size={15} />Apri nel grafo relazioni</Link>
      </div>
      <Card className="p-6 mb-6" data-testid="people-report-summary">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          {[['Codice fiscale', cf], ['Nascita', [det.dateOfBirth && fmtDate(det.dateOfBirth), det.addressOfBirth?.city].filter(Boolean).join(' · ') || '—'], ['Indirizzo', det.address?.simpleValue || '—'], ['Acquistato il', fmtDateTime(doc.created_at)]].map(([l, v]) => (
            <div key={l}><p className="text-[11px] uppercase tracking-[0.15em] text-[#5A5F67]">{l}</p><p className="text-[#F2F2F2] mt-1 font-mono text-xs">{v}</p></div>
          ))}
        </div>
        <div className={`mt-5 p-3 border rounded-sm flex flex-wrap items-center gap-3 ${anyNeg ? RED : GREEN}`} data-testid="people-report-flags">
          {anyNeg ? <ShieldAlert size={18} /> : <ShieldCheck size={18} />}
          <span className="text-sm font-semibold" data-testid="people-report-neg-outcome">{anyNeg ? 'Negatività presenti' : 'Nessuna negatività'}</span>
          <div className="flex flex-wrap gap-2">{FLAGS.map(([k, l]) => <Flag key={k} on={!!ad[k]} label={l} testid={`people-flag-${k}`} />)}</div>
        </div>
      </Card>
      <Companies title="Cariche attuali" items={r.directorships?.current || []} testid="people-report-current" />
      <Companies title="Cariche precedenti e società cessate" items={r.directorships?.previous || []} testid="people-report-previous" />
    </div>
  );
}
